// Functions organized by input type
// `gives` mirrors the PHP getFunctionRegistry() — lists the template variables each function produces
export const FUNCTIONS_BY_TYPE = {
  scene: [
    {
      function: 'get_scene_text',
      label: 'Scene Text',
      description: 'Full scene content',
      gives: ['scene_text']
    },
    {
      function: 'get_scene_summary',
      label: 'Scene Summary',
      description: 'Scene summary',
      gives: ['scene_summary']
    },
    {
      function: 'get_scene_themes',
      label: 'Scene Themes',
      description: 'Themes in this scene',
      gives: ['scene_themes']
    },
    {
      function: 'get_scene_characters',
      label: 'Scene Characters',
      description: 'Characters in this scene',
      gives: ['scene_characters']
    },
    {
      function: 'get_scene_emotions',
      label: 'Scene Emotions',
      description: 'Emotional content',
      gives: ['scene_emotions']
    },
    {
      function: 'get_scene_title',
      label: 'Scene Title',
      description: 'Scene title/heading',
      gives: ['scene_title']
    },
    {
      function: 'get_scene_setting',
      label: 'Scene Setting',
      description: 'Scene location',
      gives: ['scene_setting']
    }
  ],

  character: [
    {
      function: 'get_character_description_and_arc',
      label: 'Character Description',
      description: 'Character description & arc',
      gives: ['character_description', 'character_arc']
    },
    {
      function: 'get_character_scene_descriptions',
      label: 'Character Scenes',
      description: 'All scenes with this character (summaries)',
      gives: ['character_scenes', 'character_scene_summaries']
    },
    {
      function: 'get_character_scene_texts',
      label: 'Character Scene Texts',
      description: 'Full scene text for every scene this character appears in',
      gives: ['character_scene_texts']
    }
  ],

  theme: [
    {
      function: 'get_theme_details',
      label: 'Theme Details',
      description: 'Complete theme analysis',
      gives: ['theme_description', 'theme_symbols', 'theme_scenes', 'theme_character_connections']
    }
  ],

  trope: [
    {
      function: 'get_trope',
      label: 'Trope Analysis',
      description: 'Trope usage and effectiveness',
      gives: ['trope_description', 'trope_examples', 'trope_usage', 'trope_effectiveness']
    }
  ],

  philosophical_conflict: [
    {
      function: 'get_philosophical_conflict_details',
      label: 'Conflict Details',
      description: 'Philosophical conflict description and resolution',
      gives: ['conflict_description', 'conflict_resolution', 'conflict_resolution_location']
    }
  ],

  internal_goal: [
    {
      function: 'get_internal_goal_details',
      label: 'Internal Goal Details',
      description: 'Internal goal description and stakes resolution',
      gives: ['internal_goal_description', 'internal_goal_stakes_resolution', 'internal_goal_resolution_location']
    }
  ],

  external_goal: [
    {
      function: 'get_external_goal_details',
      label: 'External Goal Details',
      description: 'External goal description and stakes resolution',
      gives: ['external_goal_description', 'external_goal_stakes_resolution', 'external_goal_resolution_location']
    }
  ]
};

/**
 * Get available functions for a variable type
 */
export function getFunctionsForType(variableType) {
  return FUNCTIONS_BY_TYPE[variableType] || [];
}

/**
 * Get all variables that functions for a given type can produce
 */
export function getGivesForType(variableType) {
  const functions = FUNCTIONS_BY_TYPE[variableType] || [];
  const allGives = [];
  for (const fn of functions) {
    if (fn.gives) {
      allGives.push(...fn.gives);
    }
  }
  return [...new Set(allGives)];
}

/**
 * Group variables by their input type
 * Used for variable preview in question builder
 */
export function getAvailableVariablesByInput(questions) {
  const grouped = {};

  questions.forEach(question => {
    const variableType = question.variable_type || 'custom';

    if (!grouped[variableType]) {
      grouped[variableType] = [];
    }

    // Add the variable with available functions
    const availableFunctions = getFunctionsForType(variableType);

    grouped[variableType].push({
      name: question.variable_name,
      type: variableType,
      functions: availableFunctions,
      question: question
    });
  });

  return grouped;
}
